import { FC, useEffect, useMemo, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { Form, message, Affix } from 'antd';
import { useMethods } from '@lhb/hook';
import { isArray, isNotEmptyAny } from '@lhb/func';
import {
  ControlType,
  echoEstimateSalesComYHT,
  echoEstimateSalesConclusionComYHT,
  formDataComsToTiled,
  formDataEcho,
  isPropertyItemVisibleEditable,
  parseValueCatch,
  relevancyShow,
  checkMatchBusinessProperty,
  echoPassengerFlowCom,
  getSubmitValuesByTiledFormData,
} from '@/common/components/business/DynamicComponent/config';
import {
  pcdIdentification,
  addressIdentification,
  AlwaysRequiredChanceIndentifications
} from '@/common/components/business/DynamicComponent/customize';
import V2Tabs from '@/common/components/Data/V2Tabs';
import Main from './Main';
import moment from 'moment';
import { radioEnum } from '@/common/components/business/DynamicComponent/components/Footprint';
import { footprintCreate, saveChancePoint } from '@/common/api/expandStore/chancepoint';
import { chancepointApprovalSave } from '@/common/api/expandStore/approveworkbench';
import { useSelector } from 'react-redux';
import V2Message from '@/common/components/Others/V2Hint/V2Message';
import { handleJNPValue } from '@/common/components/business/DynamicComponent/components/CalculationJNP/components/transfer2form';
import { checkDynamicFormUploadStatus } from '../../../ts-config';
import { getTaskAssociable } from '@/common/components/business/DynamicComponent/components/AreaProtection/collision';
import { getModelSyncFields, mergeModelSyncFields, ModelSyncOptions } from '../../../modelSync';
import { applyYiMingChanceFieldRules } from '@/common/components/business/DynamicComponent/yiMingChancepoint';

/**
 * 简易延时，用于校验重试
 * @description 以 use 前缀自定义 Hook 形式定义，业务函数需按项目约定收敛在 useMethods / Hook 内，避免 TSX 顶层裸函数。
 */
const useDelay = () => (ms: number) => new Promise((resolve) => { setTimeout(resolve, ms); });

/**
 * 等待表单内动态字段完成渲染与注册后再继续
 * @description
 *   动态表单字段是在 formData 设置后才逐步挂载的，挂载期间部分动态组件会各自触发
 *   validateFields（其中含 100ms 防抖），此时发起的校验会被顶掉（outOfDate）。
 *   这里先等两帧让渲染落地，再留出防抖余量。
 */
const useWaitForFormSettled = (delay: (ms: number) => Promise<unknown>) => async () => {
  await new Promise<void>((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
  await delay(300);
};

/**
 * TODO(2026-1029迭代/机会点表单字段必填/可见/可写，文档：https://confluence.lanhanba.com/pages/viewpage.action?pageId=172107370)：
 * 评估机会点动态表单初始化、编辑/审批模式下字段必填、可见、可写规则及保存前校验的统一改动点。
 *
 * 机会点详情-可编辑模式表单
 * @Description
 * 逻辑说明：
 * 1. 利用antd的Form组件的onValuesChange监听输入的变化
 * 2. 从接口获取到的组件数据，通过递归将所有组件平铺到一个对象数组中
 * 3. 根据每个表单项绑定的name（即identification），在平铺的组件对象中查找对应的组件，然后进行textValue的赋值
 */
const ChanceForm: FC<{
  id,
  chanceDetail,
  isApproval,
  approvalId,
  onSearch,
  update,
  setHintStr,
  setHasEditableProperty,
  container,
  dynamicTabActiveRef,
  dynamicTabContentRefs,
  dynamicTabsRef,
  dynamicTabsActive,
  // fixedHeight,
  setDynamicTabsActive,
  /** 禁用开店区域点击查看详情 */
  disabledArea,
  /** 当前区域保护特殊申请状态；未配置区域保护时保持 undefined。 */
  antiCollisionApprovalStatus?: number,
  /** 区域保护检测结果变更后，更新详情顶部操作 */
  onAreaProtectionCollisionChange?: (hasCollisionWithin200m: boolean | undefined) => void,
  /** 保存请求进行中时接管外层 Spin，避免重复点击保存/提交审批 */
  setLoading?: (loading: boolean) => void,
  /**
   * 表单就绪后自动执行一次模型同步（屏幕外复用表单时使用）
   * @description 表单初始化是异步的，交由本组件内部判断时机，避免外部编排时序出错
   */
  autoUpdateModule?: boolean,
  /** 模型同步结束后的回调，success 表示保存接口是否成功 */
  onUpdateModuleComplete?: (success: boolean) => void,
  /** 是否在模型同步保存成功后显示成功提示；审批前置同步不显示。 */
  showModelSyncSuccess?: boolean,
  ref?: any,
}> = forwardRef(({
  id,
  chanceDetail,
  isApproval,
  approvalId,
  onSearch,
  update,
  setHintStr,
  setHasEditableProperty,
  container,
  dynamicTabActiveRef,
  dynamicTabContentRefs,
  dynamicTabsRef,
  dynamicTabsActive,
  // fixedHeight,
  setDynamicTabsActive,
  disabledArea, // 禁用开店区域点击查看详情
  antiCollisionApprovalStatus,
  onAreaProtectionCollisionChange,
  setLoading, // 保存请求进行中时接管外层 Spin
  autoUpdateModule = false,
  onUpdateModuleComplete,
  showModelSyncSuccess = true,
}, ref) => {
  // 外部调用保存机会点
  useImperativeHandle(ref, () => ({
    saveChance: (needCheck: boolean, cb: Function, shouldRefreshAfterSave = true) => {
      saveHandle(needCheck, cb, shouldRefreshAfterSave);
    },
    updateModule: (onComplete?: (success: boolean) => void, options?: ModelSyncOptions) => {
      updateModule(onComplete, options);
    },
  }));

  const dynamicOtherFormNameArr = useSelector((state: any) => state.common.dynamicOtherFormNameArr); // 存放“其他”表单的formName
  const tenantCheckData = useSelector((state: any) => state.common.tenantCheck);

  const tiledFormDataRef = useRef<any[]>([]); // 所有组件字段平铺后的对象数组
  const onValuesChangeRef = useRef<any>(null);
  const initialModelFieldsRef = useRef<any[]>([]);
  const lockRef = useRef(false);
  const autoUpdateStartedRef = useRef(false); // 屏幕外复用表单时，保证自动模型同步只执行一次
  const dynamicForms:any = useRef(null);
  const [form] = Form.useForm();
  // 校验重试用的延时与「等待动态字段挂载完成」，按项目约定以 Hook 形式定义
  const delay = useDelay();
  const waitForFormSettled = useWaitForFormSettled(delay);
  const [formData, setFormData] = useState<any>({}); // 接口返回的动态表单源数据
  const [tabActive, setTabActive] = useState<string>(''); // 当前tab值
  // supportDirectApproval 是否支持直接提交审批 1:支持 2:不支持
  const [supportDirectApproval, setSupportDirectApproval] = useState<number>(2);
  const [isFootprintValue, setIsFootprintValue] = useState<boolean>(false);// 编辑状态下踩点任务是否已创建
  const [isAreaProtect, setIsAreaProtect] = useState<boolean>(false);// 区域保护是否存在
  const [allComps, setAllComps] = useState<any[]>([]);
  // const [scrollAccordingTab, setScrollAccordingTab] = useState<boolean>(false); // 是否按照tab点击滚动页面，第一次点击tab后才为true，避免初始化内容后就滚动

  /**
     * @description 是否益禾堂租户
     */
  const isYiHeTang = useMemo(() => {
    return !!tenantCheckData?.isYiHeTang;
  }, [tenantCheckData]);

  /**
   * @description 机会点表单中提取表单详情
   */
  useEffect(() => {
    if (!isNotEmptyAny(chanceDetail)) return;

    const {
      customerTypeId,
      dynamicRelationId,
      id,
      supportDirectApproval,
      moduleDetails,
    } = chanceDetail;

    const module = isArray(moduleDetails) ? moduleDetails.find((itm: any) => itm.moduleType === 4) : {};
    if (!module || !module.infoModule) return;

    const formDetail = {
      customerTypeId,
      dynamicRelationId,
      id,
      supportDirectApproval,
      templateDetailResponse: module.infoModule,
    };
    setIsAreaProtect(!!chanceDetail?.protectInfo);
    onFormDetail(formDetail);
  }, [chanceDetail]);

  useEffect(() => {
    // 租户信息与机会点详情均为异步数据，租户信息后返回时补执行一次一鸣字段计算。
    if (!tenantCheckData?.isYiMing || !formData || !Object.keys(formData).length || !tiledFormDataRef.current.length) return;
    applyYiMingChanceFieldRules({ form, tiledFormData: tiledFormDataRef.current });
  }, [tenantCheckData?.isYiMing, formData]);

  // 一级菜单
  const tabs = useMemo(() => {
    if (!Object.keys(formData).length) return [];
    const { propertyGroupVOList } = formData;
    if (isArray(propertyGroupVOList) && propertyGroupVOList.length) {
      return propertyGroupVOList.map((item: any) => ({
        label: item.name,
        key: `${item.id}`, // tabs组件的activeKey是string类型
      }));
    }
    return [];
  }, [formData]);

  useEffect(() => {
    dynamicTabsRef && (dynamicTabsRef.current = tabs);
  }, [tabs]);

  // 父容器滚动到对应位置时设置tabs选中项
  useEffect(() => {
    if (!dynamicTabsActive) return;
    setTabActive(dynamicTabsActive);
  }, [dynamicTabsActive]);

  /**
   * @description 是否以长列表展示，总字段数<=120时使用
   */
  const isLongList = useMemo(() => {
    const res = isArray(allComps) && allComps.length <= 120;
    return res;
  }, [allComps]);

  const {
    onFormDetail,
    addressComWhenEdit,
    tabChange,
    confirmClose,
    saveHandle,
    updateModule,
    handleFootprintValue,
  } = useMethods({
    /**
     * @description 解析表单详情
     */
    onFormDetail(formDetail: any) {
      // dynamicRelationId需要特殊赋值
      const { templateDetailResponse, dynamicRelationId, supportDirectApproval, id: chanceId } = formDetail;
      templateDetailResponse.dynamicRelationId = dynamicRelationId;
      templateDetailResponse.chanceId = chanceId;
      const { propertyGroupVOList } = templateDetailResponse;
      // 设置tabs默认值
      if (isArray(propertyGroupVOList) && propertyGroupVOList.length) {
        setTabActive(`${propertyGroupVOList[0].id}`);
        tiledFormDataRef.current = []; // 设计不完美，方法有可能多次被触发，暂对策需清空数组
        formDataComsToTiled(propertyGroupVOList, tiledFormDataRef.current); // 将组件对象平铺到数组中
        // 表单赋值
        const allComs = tiledFormDataRef.current;
        setAllComps(allComps);
        addressComWhenEdit(allComs); // 特殊逻辑，拿到省市区组件的值后，将城市信息塞到地址组件中
        relevancyShow(allComs); // 关联显示
        const values = {};
        allComs.forEach((item: any) => {
          const { identification, controlType } = item;
          // 根据组件类型，将表单数据set到form上
          values[identification] = formDataEcho(item);
          // 设置踩点组件数据回显
          handleFootprintValue(item);
          handleJNPValue(item, form);
          if (controlType === ControlType.ESTIMATE_SALES_YHT.value) {
            echoEstimateSalesComYHT(parseValueCatch(item), form);
          } else if (controlType === ControlType.ESTIMATE_SALES_CONCLUSION_YHT.value) {
            echoEstimateSalesConclusionComYHT(parseValueCatch(item), form);
          } else if (controlType === ControlType.PASSENGER_FLOW.value) {
            echoPassengerFlowCom(parseValueCatch(item), form);
          }
        });

        form.setFieldsValue(values);
        if (tenantCheckData?.isYiMing) applyYiMingChanceFieldRules({ form, tiledFormData: tiledFormDataRef.current });
        initialModelFieldsRef.current = tiledFormDataRef.current.map(item => ({ ...item }));

        // 判断是否至少有一个字段可见且可写
        const hasEditableItem = isArray(allComs) && allComs.some((itm:any) => isPropertyItemVisibleEditable({
          propertyItem: itm,
          supportDirectApproval: formDetail.supportDirectApproval,
          isChancepoint: true, // 详情页面内支持动态表单编辑的一定是机会点表单
          isTraditionEvaluation: false, // 是否传统意义上的点位评估。详情页面内支持动态表单编辑的是机会点表单，或者点位评估中的特例一鸣机会点点位评估，支持动态编辑
        }));
        setHasEditableProperty && setHasEditableProperty(hasEditableItem);
      }
      setSupportDirectApproval(supportDirectApproval);
      // 与创建抽屉一致，保留默认资源项目查询所需的任务上下文。
      setFormData({ ...templateDetailResponse, resourceProjectTaskId: formDetail.taskId });
      // console.log('做成templateDetailResponse', templateDetailResponse);
    },
    /**
     * @description 获取表单详情
     */
    // getDetail() {
    //   getFormDetail({ id }).then((res: any) => {
    //     console.log('onFormDetail getdetail', res);
    //     res && onFormDetail(res);
    //   });
    // },
    addressComWhenEdit(allComs: any[]) {
      // 编辑时的地址组件的特殊逻辑处理
      const pcdCom = allComs.find((itemCom: any) => itemCom.identification === pcdIdentification);
      const addressCom = allComs.find((itemCom: any) => itemCom.identification === addressIdentification);
      if (!(pcdCom && addressCom)) return;
      const pcdVal = parseValueCatch(pcdCom);
      const addressVal = parseValueCatch(addressCom);
      if (!pcdVal) return;
      if (addressVal) {
        addressCom.textValue = JSON.stringify(Object.assign({}, addressVal, pcdVal));
      }
    },
    tabChange(active: string) {
      // setScrollAccordingTab(true);
      setTabActive(active);
      setDynamicTabsActive && setDynamicTabsActive(active);
      dynamicTabActiveRef && (dynamicTabActiveRef.current = active);
      if (dynamicTabContentRefs) {
        const targetEle = dynamicTabContentRefs.current?.find((refItem) => refItem.key === active);
        // const topVal = (targetEle?.el?.offsetTop || 0) + (dynamicForms.current?.offsetTop || 0);
        // container?.current?.scrollTo({
        //   top: (topVal - (fixedHeight || 0)),
        //   behavior: 'instant'
        // });

        // 之前的scrollTo没生效，使用scrollIntoView
        targetEle.el.scrollIntoView({ behavior: 'instant', block: 'start' });
      }
    },
    // 处理保存参数
    dealParams() {
      const propertyValues = getSubmitValuesByTiledFormData(tiledFormDataRef.current);

      if (isApproval) { // 审批详情页保存
        return {
          approvalId,
          /* 属性值 [{ id, propertyId, textValue }] */
          propertyValues,
        };
      } else { // 机会点详情页保存
        return {
          id, // 机会点 id
          dynamicRelationId: formData.dynamicRelationId,
          /* 属性值 [{ id, propertyId, textValue }] */
          propertyValues,
          taskAssociable: getTaskAssociable(propertyValues, antiCollisionApprovalStatus, !!tenantCheckData?.isSjtb),
        };
      }
    },

    /**
     * @description 表单校验（带竞态重试）
     *
     *   rc-field-form 中，若一次校验尚未结束又发起了新的 validateFields，先发起的那次会以
     *   errorFields 为空数组、outOfDate 为 true 的结果 reject —— 这不代表字段校验未通过。
     *   此处短暂等待后重试，避免把「校验被顶掉」误判为「校验不通过」。
     */
    async validateFieldsWithRetry(nameList?: any[], retries = 3) {
      try {
        return await form.validateFields(nameList as any);
      } catch (errorInfo: any) {
        const hasFieldErrors = isArray(errorInfo?.errorFields) && errorInfo.errorFields.length > 0;
        if (errorInfo?.outOfDate && !hasFieldErrors && retries > 0) {
          await delay(120);
          return this.validateFieldsWithRetry(nameList, retries - 1);
        }
        throw errorInfo;
      }
    },
    /**
     * @description 表单提交，目前的写法只能校验当前表单页的必填项，因为渲染的时候只渲染对应的tab下的表单项
     * @param needCheck 是否做完整表单必填项检查，注意false时也需要检查机会点名称等特殊字段
     * @param cb 表单保存成功的回调，带上回调参数propertyValues供“发起审批”以及“通过审批”操作用
     * @return
     */
    saveHandle(needCheck: boolean, cb: Function, shouldRefreshAfterSave = true, onComplete?: (success: boolean) => void) {
      // 无论任何情况下，机会点表单必须检查必填的特殊字段
      let checkFields: any[] = [];
      if (!needCheck && isArray(tiledFormDataRef.current)) {
        const allIndentifications = tiledFormDataRef.current.map(itm => itm.identification);
        checkFields = AlwaysRequiredChanceIndentifications.filter((itm: any) => allIndentifications.includes(itm));
        // 普通保存也校验模板中必填的树形选择，其他类型保持原有校验范围。
        checkFields = checkFields.concat(tiledFormDataRef.current
          .filter(itm => itm.controlType === ControlType.TREE_SELECT.value && itm.required && itm.isShow !== false)
          .map(itm => itm.identification));
      }
      const uploadComs = tiledFormDataRef.current?.filter((itm: any) => itm.controlType === ControlType.UPLOAD.value);
      if (!checkDynamicFormUploadStatus(uploadComs)) {
        onComplete?.(false);
        return;
      }

      // 提交审批场景下，需要检查匹配商圈组件值（目前只有益禾堂租户会检查其中的集客点是否已选择）
      if (needCheck && !checkMatchBusinessProperty(isYiHeTang, tiledFormDataRef.current)) {
        V2Message.warning('请先选择商圈内集客点'); // 产品定的文案
        onComplete?.(false);
        return;
      }

      // 全部字段检查时传undefined即无入参
      this.validateFieldsWithRetry(needCheck ? undefined : [...checkFields, ...dynamicOtherFormNameArr])
        .then(async () => {
          await this.saveInner(cb, shouldRefreshAfterSave, onComplete);
        })
        .catch((errorInfo: any) => {
          // console.log(`errorInfo 123`, errorInfo);
          if (isArray(errorInfo?.errorFields) && errorInfo?.errorFields.length && isArray(errorInfo?.errorFields[0].errors) && errorInfo?.errorFields[0].errors.length) {
            V2Message.warning(errorInfo?.errorFields[0].errors[0]);
          }
          onComplete?.(false);
        });
    },
    /** 重新计算一鸣指定字段后，复用详情表单的保存逻辑。 */
    updateModule(onComplete?: (success: boolean) => void, options: ModelSyncOptions = {}) {
      if (options.latestDetail) {
        const changed = mergeModelSyncFields(tiledFormDataRef.current, initialModelFieldsRef.current, getModelSyncFields(options.latestDetail));
        // 只回填确实更新的字段，避免重置用户输入和复杂组件的内部状态。
        const values = {};
        changed.forEach(item => { values[item.identification] = formDataEcho(item); });
        if (changed.length) form.setFieldsValue(values);
        initialModelFieldsRef.current = initialModelFieldsRef.current.map(item => {
          const updated = changed.find(field => field.identification === item.identification && field.propertyId === item.propertyId);
          return updated ? { ...item, textValue: updated.textValue } : item;
        });
      }
      ['ym1YYRJYJ', 'ymKDJ'].forEach((identification) => {
        onValuesChangeRef.current?.({
          [identification]: form.getFieldValue(identification),
        });
      });
      this.saveHandle(false, () => {
        if (options.showModelSyncSuccess !== false) V2Message.success('模型同步成功');
      }, options.shouldRefreshAfterSave ?? true, onComplete);
    },
    async saveInner(cb: Function, shouldRefreshAfterSave = true, onComplete?: (success: boolean) => void) {
      // 拿到踩点组件的值，校验信息是否填写完全（对目前的写法只能校验当前表单页的必填项的校验补充）
      const footprintItem:any = tiledFormDataRef.current.filter((item) => {
        return item.identification === 'locationFootprint';
      });
      let footprintValue;
      if (footprintItem.length !== 0 && isArray(footprintItem)) {
        footprintValue = JSON.parse(footprintItem[0]?.textValue || null);
      }
      // 与产品大鹏确认，对于审批流程配置和可见可写不为空配置中，在supportDirectApproval===1情况下（支持审批流程配置)仅当access===2（可写）的状态下需要校验踩点组件是否不为空；supportDirectApproval===2情况下，表单都可见，所以也需要判断是否填写踩点组件
      let access;
      if (footprintItem.length !== 0 && isArray(footprintItem)) {
        access = JSON.parse(footprintItem[0]?.access || null);
      }
      // footprintItem不为空数组，选了是没填消息，或者没选
      if ((isArray(footprintItem) && footprintItem.length) &&
          ((supportDirectApproval === 1 && access === 2) || supportDirectApproval === 2) &&
          (footprintValue?.dynamicComponent_footprint_isStart === radioEnum.AGREE || !isNotEmptyAny(footprintValue))) {
        // 判断踩点组件信息是否填写完全
        if (!isNotEmptyAny(footprintValue?.dynamicComponent_footprint_checkRule) ||
                    !isNotEmptyAny(footprintValue?.dynamicComponent_footprint_checkPeriod)) {
          message.error('踩点信息填写不完全');
          onComplete?.(false);
          return;
        }
      }

      if (lockRef.current) {
        onComplete?.(false);
        return;
      }
      lockRef.current = true;
      /*
        保存请求期间接管外层 Spin，使详情整体进入 loading 态（antd 的 .ant-spin-blur 带
        pointer-events: none），避免保存/提交审批按钮被重复点击。
        置于表单校验等提前 return 之后，确保不会出现遮罩无法收回的情况。
      */
      setLoading?.(true);

      let saveSuccess = false;
      try {
        const api = isApproval ? chancepointApprovalSave : saveChancePoint;
        const params = this.dealParams();
        const data = await api(params);

        // 拿到机会点的 id=>data，需要创建踩点任务 且不是在编辑机会点情况下 再创建踩点任务
        if (footprintValue?.dynamicComponent_footprint_isStart === radioEnum.AGREE && !isFootprintValue) {
          if (!data) {
            message.error('机会点尚未保存');
            return;
          }
          await footprintCreate({
            chancePointId: data,
            propertyValues: params.propertyValues,
            checkRule: footprintValue.dynamicComponent_footprint_checkRule,
            checkPeriod: [{
              start: footprintValue.dynamicComponent_footprint_checkPeriod[0],
              end: footprintValue.dynamicComponent_footprint_checkPeriod[1],
            }]
          });
        }
        if (shouldRefreshAfterSave) {
          // 编辑时更新机会点详情
          update && update();
          onSearch && onSearch(id || {}); // 刷新列表
        }
        // 传回propertyValues，发起审批请求中使用，通过审批目前用不到这个
        cb && cb(params.propertyValues);
        // 详情页编辑表单时保存成功后不需要关闭，所以注释掉

        if (!isApproval && shouldRefreshAfterSave) {
          confirmClose(); // 非审批时需要清空数据
        }
        saveSuccess = true;
      } finally {
        // 踩点任务创建也在锁的覆盖范围内，避免保存接口返回后出现无锁窗口
        lockRef.current = false;
        setLoading?.(false);
        onComplete?.(saveSuccess);
      }
    },
    confirmClose() {
      form.resetFields(); // 表单清空
      tiledFormDataRef.current = [];
      setAllComps([]);
    },
    // 处理踩点组件回显
    handleFootprintValue(item) {
      // locationFootprint 后端返回的踩点组件唯一标识
      if (item.identification === 'locationFootprint') {

        const textValue = JSON.parse(item?.textValue || null);
        if (!isNotEmptyAny(textValue)) return;
        // 判断是否已经有踩点任务
        setIsFootprintValue(textValue.dynamicComponent_footprint_isStart === radioEnum.AGREE);

        const values = {};
        // 数据回显
        values['dynamicComponent_footprint_isStart'] = textValue.dynamicComponent_footprint_isStart;
        form.setFieldsValue(values);
        // 如果踩点任务未创建，就不往下进行
        if (textValue.dynamicComponent_footprint_isStart === radioEnum.DENY) {
          return;
        }
        // 回显踩点日期规则
        values['dynamicComponent_footprint_checkRule'] = textValue?.dynamicComponent_footprint_checkRule;
        // 回显踩点时间
        values['dynamicComponent_footprint_checkPeriod'] =
        [
          moment(textValue?.dynamicComponent_footprint_checkPeriod[0], 'HH:mm'),
          moment(textValue?.dynamicComponent_footprint_checkPeriod[1], 'HH:mm'),
        ];
        form.setFieldsValue(values);

      }
    },
  });

  /**
   * @description 屏幕外复用表单时，待表单字段平铺完成后自动执行一次模型同步
   *
   *   表单详情解析是异步过程，交由本组件内部判断时机，避免外部编排时序出错。
   *   updateModule 内部会重算一鸣字段并复用详情表单的保存逻辑，保存结束后回调通知外层。
   */
  useEffect(() => {
    if (!autoUpdateModule || autoUpdateStartedRef.current) return;
    if (!Object.keys(formData).length || !tiledFormDataRef.current.length) return;
    autoUpdateStartedRef.current = true;
    let cancelled = false;
    (async () => {
      // 等待 V2Form 内动态字段渲染注册完毕，避免本次校验被挂载期的校验调用顶掉
      await waitForFormSettled();
      if (cancelled) return;
      updateModule((success: boolean) => {
        onUpdateModuleComplete?.(success);
      }, { showModelSyncSuccess });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoUpdateModule, formData]);

  return (
    <div ref={dynamicForms}>
      <Affix
        offsetTop={0}
        target={() => container?.current}
      >
        <V2Tabs items={tabs} activeKey={tabActive} onChange={tabChange} />
      </Affix>
      <Main
        form={form}
        formData={formData}
        // isEvaluation={false}
        // needCheck={true}
        supportDirectApproval={supportDirectApproval}
        setFormData={setFormData}
        active={tabActive}
        // scrollAccordingTab={scrollAccordingTab}
        dynamicTabContentRefs={dynamicTabContentRefs}
        isLongList={isLongList}
        setHintStr={setHintStr}
        tiledFormDataRef={tiledFormDataRef}
        onValuesChangeRef={onValuesChangeRef}
        isFootprintValue={isFootprintValue}
        isAreaProtect={isAreaProtect}
        disabledArea={disabledArea}
        antiCollisionApprovalStatus={antiCollisionApprovalStatus}
        onAreaProtectionCollisionChange={onAreaProtectionCollisionChange}
      />
    </div>
  );
});

export default ChanceForm;
